import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

export interface PaginationProps {
  page: number;
  limit: number;
  total: number;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  /** Page-size choices. Defaults to [25, 50, 100]. */
  pageSizeOptions?: number[];
}

const SELECT_CLASS =
  'flex h-8 rounded-md border border-input bg-background pl-2 pr-1 text-xs shadow-sm ' +
  'transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring ' +
  'disabled:cursor-not-allowed disabled:opacity-50';

/**
 * Controlled pagination control: "X–Y of Z", Prev/Next (disabled at the ends),
 * and a page-size selector. Generalizes the inline footer in AuditLogPage.
 */
export function Pagination({
  page,
  limit,
  total,
  onPageChange,
  onLimitChange,
  pageSizeOptions = [25, 50, 100],
}: PaginationProps) {
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const rangeStart = total === 0 ? 0 : (page - 1) * limit + 1;
  const rangeEnd = Math.min(page * limit, total);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 mt-4 text-sm text-muted-foreground">
      <div className="flex items-center gap-3">
        <span className="tabular-nums">
          {rangeStart}–{rangeEnd} of {total}
        </span>
        <label className="flex items-center gap-1.5">
          <span className="text-xs">Per page</span>
          <select
            aria-label="Rows per page"
            value={limit}
            onChange={(e) => onLimitChange(Number(e.target.value))}
            className={SELECT_CLASS}
          >
            {pageSizeOptions.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="flex items-center gap-1.5">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          className="h-8 px-2 text-xs"
        >
          <ChevronLeft className="h-4 w-4" />
          Prev
        </Button>
        <span className="px-2 text-xs tabular-nums">
          {page} / {totalPages}
        </span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
          className="h-8 px-2 text-xs"
        >
          Next
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
